import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { useUser } from '@/contexts/UserContext'
import type { SetupStatus } from '@/types/setup-status'

// TODO test: hook devuelve isLoading=true inicialmente, false después del fetch
// TODO test: hook devuelve status correctamente tipado cuando el API responde 200
// TODO test: hook devuelve error cuando el API responde 4xx/5xx
// TODO test: invalidate() dispara un re-fetch y resetea error
// TODO test: re-fetch se dispara al volver la pestaña al foco (visibilitychange)
// TODO test: no fetcha si el componente es desmontado antes de que la respuesta llegue (cancelación)

export function useSetupStatus() {
  const { activeEmpresaId } = useUser()
  const [status, setStatus] = useState<SetupStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchStatus = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await api.get<SetupStatus>('/me/empresa/setup-status')
      setStatus(data)
    } catch (e) {
      const err =
        e instanceof ApiError
          ? e
          : new Error('Error al cargar el estado de configuración')
      setError(err)
      setStatus(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Fetch inicial al montar y cada vez que cambia la EMPRESA ACTIVA.
  // El setup-status es POR-EMPRESA (el backend resuelve "mi empresa" desde el
  // token empresa-scoped). Al crear o cambiar de empresa desde el switcher el
  // OnboardingGuard NO se desmonta, así que sin esta dependencia se quedaría con
  // el estado de la empresa anterior y dejaría pasar una empresa nueva/incompleta
  // sin forzar el onboarding. Reseteamos status a null para que el guard muestre
  // su spinner (en vez de un flash del dashboard con datos de la empresa vieja)
  // mientras recalcula.
  useEffect(() => {
    setStatus(null)
    fetchStatus()
  }, [fetchStatus, activeEmpresaId])

  // RF-C4-06: re-fetch cuando la pestaña vuelve a estar visible.
  // Útil cuando el usuario navega a una sub-página del wizard, completa un paso,
  // y vuelve al dashboard o al wizard — el estado se refresca automáticamente.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchStatus()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [fetchStatus])

  return {
    status,
    isLoading,
    error,
    invalidate: fetchStatus,
  }
}
